import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { IoChevronBack, IoChevronDownOutline } from 'react-icons/io5';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { MdReportProblem } from 'react-icons/md';
import { BsArrowUpShort } from 'react-icons/bs';
import LocationIcon from '../../icons/LocationIcon';
import Header from '../../components/Header/Header';
import { TILE_URL, TILE_ATTRIBUTION, TILE_MAX_ZOOM } from '../../config/mapTiles';
import DataTable from '../../components/DataTable/DataTable';
import { useDeviceData } from '../../hooks/useDeviceData';
import { useAppsByGrade } from '../../hooks/useAppsByGrade';
import { useDeviceIssues } from '../../hooks/useDeviceIssues';
import { ENDPOINTS, timeAgo, formatDateTime } from '../../api/api';
import styles from './Device.module.css';

const PIN_ICON = L.divIcon({
  className: '',
  html: ReactDOMServer.renderToStaticMarkup(<LocationIcon color="#E53535" />),
  iconSize: [34, 40],
  iconAnchor: [17, 40],
});

const GRADE_APP_COLUMNS = [
  { key: 'name',      label: 'Application' },
  { key: 'version',   label: 'Version' },
  { key: 'installs',  label: 'Installs' },
  { key: 'adoption',  label: 'Adoption' },
];

const APP_COLUMNS = [
  { key: 'name',       label: 'Name' },
  { key: 'package',    label: 'Package' },
  { key: 'version',    label: 'Version' },
  { key: 'wifiData',   label: 'WiFi Data' },
  { key: 'mobileData', label: 'Mobile Data' },
];

function fmtBytes(bytes) {
  if (!bytes) return '0 MB';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  return `${(bytes / 1048576).toFixed(0)} MB`;
}


function Device() {
  const { districtSlug, circuitSlug, schoolSlug, imei } = useParams();
  const navigate = useNavigate();

  const { loading, error, device, refresh } = useDeviceData(imei);

  const learnerGrade = device?.grade && device.grade !== '—' ? device.grade : 'All';
  const { apps: gradeApps, loading: gradeAppsLoading } = useAppsByGrade(learnerGrade);
  const { issues, loading: issuesLoading, reload: reloadIssues, resolve: resolveIssue } = useDeviceIssues(imei);

  const [showReport, setShowReport] = useState(false);
  const [reportStatus, setReportStatus] = useState('');
  const [reportComment, setReportComment] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState('');

  const breadcrumbs = [
    { label: 'Overview', path: '/' },
    { label: 'District', path: `/district/${districtSlug}` },
    { label: 'Circuit',  path: `/district/${districtSlug}/circuit/${circuitSlug}` },
    ...(schoolSlug ? [{ label: 'School', path: `/district/${districtSlug}/circuit/${circuitSlug}/school/${schoolSlug}` }] : []),
    { label: 'Device' },
  ];

  if (loading) {
    return (
      <>
        <Header breadcrumbs={breadcrumbs} />
        <div className={styles.page} style={{ color: 'var(--color-text-secondary)', paddingTop: '2rem' }}>Loading device…</div>
      </>
    );
  }

  if (error || !device) {
    return (
      <>
        <Header breadcrumbs={breadcrumbs} />
        <div className={styles.page} style={{ color: '#E53535', paddingTop: '2rem' }}>{error || 'Device not found'}</div>
      </>
    );
  }

  async function submitReport() {
    if (!reportStatus) { setReportError('Please select an issue type.'); return; }
    setReportSubmitting(true);
    setReportError('');
    try {
      const res = await fetch(ENDPOINTS.reportIssue, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imeiNumber: device.imeiNumber,
          deviceTag:  device.imeiNumber,
          branch:     device.branch,
          circuit:    device.circuit !== '—' ? device.circuit : '',
          district:   device.district !== '—' ? device.district : '',
          school:     device.school !== '—' ? device.school : '',
          emiscode:   device.emiscode,
          issue:      reportStatus,
          comment:    reportComment,
        }),
      });
      if (!res.ok) throw new Error('Failed to submit');
      setShowReport(false);
      setReportStatus('');
      setReportComment('');
      reloadIssues();
    } catch {
      setReportError('Failed to submit. Please try again.');
    } finally {
      setReportSubmitting(false);
    }
  }

  const storageUsedPct = device.storageTotal > 0 ? (device.storageUsed / device.storageTotal) * 100 : 0;
  const appRows = device.appUsageData
    .filter((a) => a.systemApp === false)
    .map((a) => ({
      name:       a.appName     || a.name    || '—',
      package:    a.packageName || a.package || '—',
      version:    a.versionName || a.version || '—',
      wifiData:   fmtBytes(a.wifiUsageBytes),
      mobileData: fmtBytes(a.dataUsageBytes),
    }));

  return (
    <>
      <Header breadcrumbs={breadcrumbs} />

      <div className={styles.page}>

        <div className={styles.titleRow}>
          <div className={styles.titleLeft}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
              <IoChevronBack size={16} />
            </button>
            <span className={styles.imei}>{imei}</span>
            <span className={styles.lastSeen}>
              Last seen: {timeAgo(device.lastReportedTime)}
              {formatDateTime(device.lastReportedTime) && (
                <span className={styles.lastSeenDate}> · {formatDateTime(device.lastReportedTime)}</span>
              )}
            </span>
          </div>
          <div className={styles.titleActions}>
            <RefreshButton onClick={refresh} loading={loading} />
            <button className={styles.reportBtn} onClick={() => { setShowReport(true); setReportError(''); }}>
              <MdReportProblem size={14} /> Report device issue
            </button>
          </div>
        </div>

        <div className={styles.metaRow}>
          {[
            { label: 'Learner Name', value: device.learnerName },
            { label: 'Grade',        value: device.grade       },
            { label: 'Gender',       value: device.gender      },
            { label: 'School',       value: device.school      },
            { label: 'District',     value: device.district    },
            { label: 'Circuit',      value: device.circuit     },
          ].map((item) => (
            <div key={item.label} className={styles.metaItem}>
              <div className={styles.metaLabel}>{item.label}</div>
              <div className={styles.metaValue}>{item.value || '—'}</div>
            </div>
          ))}
        </div>

        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Battery</div>
            <div className={styles.statBottom}>
              <div className={styles.statValue}>{device.battery}%</div>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${device.battery}%`, background: '#3FB760' }} />
              </div>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statLabel}>Storage Used</div>
            <div className={styles.statBottom}>
              <div>
                <div className={styles.statValue}>{device.storageUsed} <span className={styles.statUnit}>GB</span></div>
                <div className={styles.statMeta}>of {device.storageTotal} GB total</div>
              </div>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${storageUsedPct}%`, background: '#3FB760' }} />
              </div>
            </div>
          </div>

          <div className={styles.carrierCard} style={{ background: device.carrierColor }}>
            <div className={styles.carrierLabel}>Mobile Carrier</div>
            <div className={styles.carrierName}>{device.carrier}</div>
          </div>
        </div>

        <div className={styles.dataRow}>
          <div className={styles.dataCard}>
            <div className={styles.dataTitle}>Mobile Data</div>
            <div className={styles.dataSplit}>
              <div className={styles.dataItem}>
                <div className={styles.dataItemLabel}>Total Used</div>
                <div className={styles.dataItemValue}>{device.mobileData}</div>
              </div>
              <div className={styles.dataItem}>
                <div className={styles.dataItemLabel}>Downloaded</div>
                <div className={styles.dataItemValue}>{device.mobileDataRx}</div>
              </div>
              <div className={styles.dataItem}>
                <div className={styles.dataItemLabel}>Uploaded</div>
                <div className={styles.dataItemValue}>{device.mobileDataTx}</div>
              </div>
            </div>
          </div>

          <div className={styles.dataCard}>
            <div className={styles.dataTitle}>WiFi Data</div>
            <div className={styles.dataSplit}>
              <div className={styles.dataItem}>
                <div className={styles.dataItemLabel}>Total Used</div>
                <div className={styles.dataItemValue}>{device.wifiData}</div>
              </div>
              <div className={styles.dataItem}>
                <div className={styles.dataItemLabel}>Downloaded</div>
                <div className={styles.dataItemValue}>{device.wifiDataRx}</div>
              </div>
              <div className={styles.dataItem}>
                <div className={styles.dataItemLabel}>Uploaded</div>
                <div className={styles.dataItemValue}>{device.wifiDataTx}</div>
              </div>
            </div>
          </div>
        </div>

        {/* <div className={styles.sectionWrapper}>
          <div className={styles.sectionTitle}>Storage</div>
          <div className={styles.storageCard}>
            <div className={styles.storageBar}>
              <div className={styles.storageSegment} style={{ width: `${storageUsedPct}%`, background: '#3FB760' }} />
              <div className={styles.storageSegment} style={{ width: `${100 - storageUsedPct}%`, background: '#505050' }} />
            </div>
            <div className={styles.storageLegend}>
              {[
                { label: 'Used', gb: device.storageUsed,  color: '#3FB760' },
                { label: 'Free', gb: device.storageFree,  color: '#505050' },
              ].map((s) => (
                <div key={s.label} className={styles.legendItem}>
                  <span className={styles.legendDot} style={{ background: s.color }} />
                  <span className={styles.legendLabel}>{s.label}</span>
                  <span className={styles.legendGb}>{s.gb} GB</span>
                </div>
              ))}
            </div>
          </div>
        </div> */}

        <div className={styles.mapRow}>
          <div className={styles.mapWrapper}>
            <MapContainer
              center={device.coords}
              zoom={7}
              className={styles.map}
              zoomControl={true}
              scrollWheelZoom={false}
              attributionControl={false}
            >
              <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} maxZoom={TILE_MAX_ZOOM} />
              <Marker position={device.coords} icon={PIN_ICON} />
            </MapContainer>
          </div>

          <div className={styles.locationPanel}>
            <div className={styles.locationTitle}>Device Info</div>
            <div className={styles.locationList}>
              {[
                { label: 'Model',        value: device.model        },
                { label: 'Manufacturer', value: device.manufacturer },
                { label: 'Serial No.',   value: device.serialNumber },
                { label: 'OS Version',   value: device.osVersion    },
                { label: 'CPU Speed',    value: device.cpuSpeed     },
              ].map((item) => (
                <div key={item.label} className={styles.locationItem}>
                  <div>
                    <div className={styles.locationAddress}>{item.label}</div>
                    <div className={styles.locationDates}>{item.value || '—'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {appRows.length > 0 && (
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionTitle}>App Usage</div>
            <DataTable
              columns={APP_COLUMNS}
              rows={appRows}
              searchable
              searchPlaceholder="Search apps"
              paginated
              pageSize={10}
            />
          </div>
        )}

        {/* <div className={styles.sectionWrapper}>
          <div className={styles.sectionTitleRow}>
            <div className={styles.sectionTitle}>Applications by Grade</div>
            {device.grade !== '—' && (
              <span className={styles.gradeTag}>Grade {device.grade}</span>
            )}
          </div>
          <DataTable
            columns={GRADE_APP_COLUMNS}
            rows={gradeAppsLoading ? [] : gradeApps.slice(0, 20).map((a) => ({
              name:     a.name    ?? '—',
              version:  a.version ?? '—',
              installs: typeof a.installs === 'number' ? a.installs.toLocaleString() : '—',
              adoption: `${a.adoption ?? 0}%`,
            }))}
            searchable
            searchPlaceholder="Search applications"
            paginated
            pageSize={10}
          />
        </div> */}

        {(issuesLoading || issues.length > 0) && (
          <div className={styles.sectionWrapper}>
            <div className={styles.sectionTitle}>Reported Issues</div>
            <div className={styles.issuesList}>
              {issuesLoading ? (
                <div className={styles.issuesEmpty}>Loading…</div>
              ) : issues.map((issue) => (
                <div key={issue._id} className={styles.issueRow}>
                  <div className={styles.issueLeft}>
                    <span className={styles.issueType}>{issue.issue}</span>
                    {issue.comment && <span className={styles.issueComment}>{issue.comment}</span>}
                    <span className={styles.issueDate}>{new Date(issue.createdAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                  <div className={styles.issueRight}>
                    <span className={`${styles.issueStatus} ${issue.status === 'resolved' ? styles.issueResolved : styles.issuePending}`}>
                      {issue.status}
                    </span>
                    {issue.status !== 'resolved' && (
                      <button
                        className={styles.resolveBtn}
                        onClick={async () => {
                          try { await resolveIssue(issue._id); }
                          catch { /* silent */ }
                        }}
                      >
                        Mark resolved
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {showReport && (
        <div className={styles.modalBackdrop} onClick={() => { setShowReport(false); setReportError(''); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>Report device issue</span>
              <button className={styles.modalClose} onClick={() => { setShowReport(false); setReportError(''); }}>✕</button>
            </div>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Issue</label>
              <div className={styles.selectWrapper}>
                <select className={styles.modalSelect} value={reportStatus} onChange={(e) => setReportStatus(e.target.value)}>
                  <option value="">Select issue</option>
                  <option value="lost">Lost</option>
                  <option value="stolen">Stolen</option>
                  <option value="damaged">Damaged</option>
                  <option value="not-working">Not working</option>
                </select>
                <IoChevronDownOutline className={styles.selectChevron} size={14} />
              </div>
            </div>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Comment</label>
              <textarea
                className={styles.modalTextarea}
                placeholder="Add a comment"
                value={reportComment}
                onChange={(e) => setReportComment(e.target.value)}
              />
            </div>

            {reportError && (
              <div style={{ color: '#E53535', fontSize: '0.82rem' }}>{reportError}</div>
            )}
            <button className={styles.submitBtn} onClick={submitReport} disabled={reportSubmitting}>
              <BsArrowUpShort size={24} /> {reportSubmitting ? 'Submitting…' : 'Submit issue'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default Device;
